import type { ComponentProps } from 'react';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Alert, Box, Chip, Divider, Link, Stack, Typography } from '@mui/material';
import {
  formatSeveritySummary,
  type EmployerReviewSummaryViewModel,
} from '../../lib/employerReview/reviewReason.types';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';
import { AmbientGlassShell, ambientGlassInnerPaperSx, EMPLOYER_SUMMARY_GLOW } from './AmbientGlassShell';
import { isEmployerReviewPathTourDismissed } from '../../lib/employerReview/employerReviewTourStorage';
import { useEmployerEvidenceTourOptional } from './guidedTour/EmployerEvidenceTourContext';
import type { EmployerFootprintAdminViewDto, EmployerVerificationDetail } from '../../api/employerVerificationDashboardApi';
import { RecommendedAction } from './AdditionalFindings';
import { ReviewConcernList } from './ReviewConcernAccordion';

function statusChipColor(
  status: EmployerReviewSummaryViewModel['status'],
): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (status) {
    case 'verified':
      return 'success';
    case 'not_confirmed':
      return 'error';
    case 'needs_review':
      return 'warning';
    case 'in_progress':
      return 'info';
    default:
      return 'default';
  }
}

function normalizeActionText(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function SummaryCardShell({
  children,
  issueCount = 0,
  ...cardProps
}: ComponentProps<typeof ApplicationSectionCard> & { issueCount?: number }) {
  const summaryGlow =
    issueCount > 0
      ? { ...EMPLOYER_SUMMARY_GLOW, intensity: 0.12, animate: false }
      : EMPLOYER_SUMMARY_GLOW;

  return (
    <Box data-tour-target="employer-review-summary">
      <AmbientGlassShell glow={summaryGlow} surface="summary">
        <ApplicationSectionCard
          {...cardProps}
          paperSx={ambientGlassInnerPaperSx}
          data-testid="employer-review-summary-card"
        >
          {children}
        </ApplicationSectionCard>
      </AmbientGlassShell>
    </Box>
  );
}

function StatusIcon({ status }: { status: EmployerReviewSummaryViewModel['status'] }) {
  switch (status) {
    case 'verified':
      return <CheckCircleOutlineRoundedIcon fontSize="small" />;
    case 'not_confirmed':
      return <ErrorOutlineRoundedIcon fontSize="small" />;
    case 'needs_review':
      return <RateReviewOutlinedIcon fontSize="small" />;
    case 'in_progress':
      return <HourglassTopRoundedIcon fontSize="small" />;
    default:
      return <VerifiedUserOutlinedIcon fontSize="small" />;
  }
}

export function EmployerReviewSummary({
  vm,
  detail = null,
  footprint = null,
}: {
  vm: EmployerReviewSummaryViewModel;
  detail?: EmployerVerificationDetail | null;
  footprint?: EmployerFootprintAdminViewDto | null;
}) {
  const nav = useEmployerEvidenceTourOptional();
  const showIssues = vm.issueCount > 0;
  const severityLine = formatSeveritySummary(vm.blockerCount, vm.concernCount);
  const topConcernAction = vm.topReasons[0]?.recommendedAction?.trim() ?? '';
  const showRecommendedAction =
    Boolean(vm.recommendedAction?.trim()) &&
    (!topConcernAction ||
      normalizeActionText(vm.recommendedAction!) !== normalizeActionText(topConcernAction));
  const iconColor =
    vm.status === 'verified'
      ? 'success'
      : vm.status === 'not_confirmed'
        ? 'error'
        : vm.status === 'needs_review'
          ? 'warning'
          : 'primary';

  if (vm.status === 'verified' && !showIssues) {
    return (
      <SummaryCardShell
        title="Employment verification"
        icon={<StatusIcon status={vm.status} />}
        iconColor={iconColor}
        endAdornment={
          <Chip
            size="small"
            label={vm.statusLabel}
            color={statusChipColor(vm.status)}
            variant="outlined"
            sx={{ height: 22, fontWeight: 700 }}
          />
        }
      >
        <Alert severity="success" icon={false} sx={{ borderRadius: 1.5, py: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
            {vm.cleanPassMessage}
          </Typography>
        </Alert>
      </SummaryCardShell>
    );
  }

  if (vm.status === 'not_started') {
    return (
      <SummaryCardShell
        title="Employment verification"
        icon={<StatusIcon status={vm.status} />}
        iconColor="primary"
        endAdornment={
          <Chip
            size="small"
            label={vm.statusLabel}
            variant="outlined"
            sx={{ height: 22, fontWeight: 700 }}
          />
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
          {vm.cleanPassMessage}
        </Typography>
      </SummaryCardShell>
    );
  }

  return (
      <SummaryCardShell
        title="Employment verification"
        icon={<StatusIcon status={vm.status} />}
        iconColor={iconColor}
        issueCount={vm.issueCount}
        endAdornment={
          <Chip
            size="small"
            label={vm.statusLabel}
            color={statusChipColor(vm.status)}
            variant="outlined"
            sx={{ height: 22, fontWeight: 700 }}
          />
        }
      >
        <Stack spacing={1.25}>
        {severityLine ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
            {severityLine}
          </Typography>
        ) : vm.cleanPassMessage ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {vm.cleanPassMessage}
          </Typography>
        ) : null}

        {showIssues ? (
          <ReviewConcernList
            primaryReasons={vm.topReasons}
            additionalReasons={vm.additionalReasons}
            detail={detail}
            footprint={footprint}
          />
        ) : null}

        {showRecommendedAction ? (
          <>
            <Divider sx={{ my: 0.25 }} />
            <RecommendedAction action={vm.recommendedAction} />
          </>
        ) : null}

        {showIssues && nav ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => nav.startReviewPathTour()}
            data-tour-target="employer-review-take-tour"
            sx={{ fontWeight: 600, fontSize: '0.8125rem', alignSelf: 'flex-start' }}
          >
            {isEmployerReviewPathTourDismissed() ? 'Replay guided tour' : 'Take guided tour'}
          </Link>
        ) : null}
      </Stack>
    </SummaryCardShell>
  );
}
